<template>
  <div class="settings-container">
    <div class="columns">
      <div class="column is-half">
        <b-field class="field">
          <b-input
            placeholder="Search Enabler(s)"
            type="search"
            v-model="searchTitle"
          >
          </b-input>
        </b-field>
      </div>
      <div :class="[$style.createNewButtonContainer, 'column', 'is-half']">
        <b-button
          @click="openModalForCreate()"
          :class="[$style.createNewButton, 'button', 'is-primary']"
          >Create New</b-button
        >
      </div>
    </div>

    <!-- Updated EnablerModal props and event handlers -->
    <EnablerModal
      :key="modalKey"
      :isModalActive="isModalActive"
      :projectId="projectId"
      :isNew="editingEntityId === null"
      :draggable="true"
      :isMinimized="isMinimized"
      :gripEnablerDetails="entityDetailsForEdit"
      @close="closeModal"
      @create="handleEnablerCreate"
      @update="handleEnablerUpdate"
      @minimize="minimizeModal"
      @restore="restoreModal"
    />

    <BaseLoading v-if="isLoading" />
    <BasePaginatorHoc
      v-else
      :component="EnablerList"
      :page.sync="page"
      :perPage.sync="perPage"
      :items="getGripEnablerList"
      :totalCount="totalCount"
      :editingEnablerId="editingEntityId"
      :isMinimized="isMinimized"
      @paginate="handlePaginator"
      @updateList="fetchData"
      @edit-enabler="openModalForEdit"
      @restore-from-overlay="restoreModal"
      @delete-enabler-request="handleDeleteRequest"
    />
  </div>
</template>

<script lang="ts">
import { Component, Watch } from 'vue-property-decorator';
import { Action, namespace, State } from 'vuex-class';
import { ToastProgrammatic as Toast } from 'buefy';
import { mixins } from 'vue-class-component';
import EnablerList from './EnablerList.vue';
import BasePaginatorHoc from '../../../components/base/BasePaginatorHoc.vue';
import {
  CreateGripEnablerBase,
  CreateGripEnablerPayload,
  GetEnablersPayload,
  GripEnabler,
  GripEnablerDTO,
  UpdateGripEnablerBase,
  UpdateGripEnablerPayload,
} from '../../../store/types/grip.types';
import EnablerModal from './EnablerModal.vue';
import {
  ApiState,
  Pagination,
  PaginatorDto,
} from '../../../store/types/general.types';
import { Debounce } from '../../../jbi-shared/util/debounce.vue-decorator';
import BaseLoading from '../../../components/base/BaseLoading.vue';
import {
  isDifferentDeep,
  isTruthy,
} from '../../../jbi-shared/util/watcher.vue-decorator';
import { showUnsavedChangesDialog } from '@/utils/dialog.util';
import DeleteModalBox from '@/components/DeleteModalBox.vue';
import GripSettingsModalManagerMixin from '@/views/Grip/components/grip-settings-modal-manager.mixin';

@Component({
  components: { BaseLoading, BasePaginatorHoc, EnablerList, EnablerModal },
})
export default class EnablerSettings extends mixins(
  GripSettingsModalManagerMixin,
) {
  public entityName: string = 'Enabler';
  public entityIdKey: string = 'enablerId';

  @Action('projects/getGripEnablers')
  public getGripEnablers!: (payload: {
    projectId: number;
    params: GetEnablersPayload;
  }) => void;

  @State((state) => state.projects.gripEnablers)
  public gripEnablers!: Pagination<GripEnabler>;

  @State((state) => state.projects.apiState.getGripEnablers)
  public getGripEnablersApiState!: ApiState;

  @Action('projects/createGripEnabler')
  public createGripEnabler!: (payload: CreateGripEnablerPayload) => void;

  @State((state) => state.projects.apiState.createGripEnabler)
  public createGripEnablerApiState!: ApiState;

  // Added action for updating enabler
  @Action('projects/updateGripEnabler')
  public updateGripEnabler!: (payload: UpdateGripEnablerPayload) => void;

  // Added state for updateGripEnabler API
  @State((state) => state.projects.apiState.updateGripEnabler)
  public updateGripEnablerApiState!: ApiState;

  @Action('projects/deleteGripEnabler')
  public deleteGripEnabler!: (payload: {
    projectId: number;
    enablerId: number;
  }) => void;

  @State((state) => state.projects.apiState.deleteGripEnabler)
  public deleteGripEnablerApiState!: ApiState;

  get getAction(): (payload: {
    projectId: number;
    params: GetEnablersPayload;
  }) => void {
    return this.getGripEnablers;
  }

  get deleteAction(): (payload: {
    projectId: number;
    enablerId: number;
  }) => void {
    return this.deleteGripEnabler;
  }

  get getApiState(): ApiState {
    return this.getGripEnablersApiState;
  }
  get createApiState(): ApiState {
    return this.createGripEnablerApiState;
  }
  get updateApiState(): ApiState {
    return this.updateGripEnablerApiState;
  }
  get deleteApiState(): ApiState {
    return this.deleteGripEnablerApiState;
  }

  get EnablerList() {
    return EnablerList;
  }

  get getGripEnablerList(): GripEnabler[] {
    return this.gripEnablers.items || [];
  }

  get totalCount(): number {
    return this.gripEnablers.totalItems || 0;
  }

  /**
   * Handles the creation of a new enabler.
   * @param {CreateGripEnablerBase} gripEnabler - Data from the modal form.
   */
  public handleEnablerCreate(gripEnabler: CreateGripEnablerBase): void {
    this.createGripEnabler({ ...gripEnabler, projectId: this.projectId });
  }

  /**
   * Handles the update of an existing enabler.
   * @param {UpdateGripEnablerBase} gripEnablerUpdateData - Data from the modal form.
   */
  public handleEnablerUpdate(
    gripEnablerUpdateData: UpdateGripEnablerBase & { id: number },
  ): void {
    if (
      this.editingEntityId !== null &&
      this.editingEntityId === gripEnablerUpdateData.id
    ) {
      const { id, ...changesToApply } = gripEnablerUpdateData;

      if (Object.keys(changesToApply).length > 0) {
        const payload: UpdateGripEnablerPayload = {
          ...gripEnablerUpdateData,
          id: this.editingEntityId,
          projectId: this.projectId,
        };
        this.updateGripEnabler(payload);
      } else {
        this.closeModal();
      }
    } else {
      this.closeModal();
    }
  }
}
</script>
<style lang="scss" module>
.createNewButtonContainer {
  display: flex;
  justify-content: flex-end;
}
.createNewButton {
  color: $white;
  background-color: $primary;
  border-radius: $size-2;
  padding: 5px $size-6;
}
</style>
